import { screenMaskedEventSchema, screenUnsharedEventSchema } from "@typedoc/interview/events";
import { z } from "zod";
import { FILE_MAX_BYTES, uploadTargetSchema } from "./files";
import { idSchema } from "./ids";
import { callerKeySchema } from "./recordings";

/**
 * The vocabulary of screen capture during a recording (ENG-1024): the still screens the desktop
 * app keeps, what it covered on each before the image left the device, and the grant that lets it
 * add them to one recording without holding a sign-in token. The desktop app, the API and the
 * package view read the same shapes.
 *
 * This module is browser-safe: it imports nothing from Node or a server.
 */

/** How long a capture grant works; the island asks for a fresh one before it runs out. */
export const CAPTURE_GRANT_SECONDS = 900;

/** How long after a recording ends its grants still add screens, so the outbox can drain. */
export const CAPTURE_GRACE_SECONDS = 600;

/**
 * How wide a screen's thumbnail is, in pixels: about five times the timeline's tiles, so it stays
 * sharp at twice their size on a high-density display, and a few tens of kilobytes as a JPEG.
 */
export const SCREEN_THUMBNAIL_WIDTH = 320;

/** The most regions one screen may have covered. */
export const SCREEN_COVERED_MAX = 16;

/** The widest or tallest stored screen the API accepts, in pixels. */
const SCREEN_SIDE_MAX = 16_384;

/** Room for floating-point sums such as 0.1 + 0.2, so a region flush with an edge is not refused. */
const EDGE_TOLERANCE = 1e-9;

const fraction = z.number().min(0).max(1);

/**
 * A part of the display, in fractions of its width and height from the top left, so it means the
 * same thing at any resolution. It lies wholly on the display.
 */
export const regionSchema = z
  .strictObject({ x: fraction, y: fraction, width: fraction, height: fraction })
  .refine(
    (region) =>
      region.x + region.width <= 1 + EDGE_TOLERANCE &&
      region.y + region.height <= 1 + EDGE_TOLERANCE,
    { error: "A region must lie on the display." },
  );
export type Region = z.infer<typeof regionSchema>;

/**
 * Why a screen was kept (ENG-1025): the person moved to another app, window or page (`context`);
 * the screen from just before a click, from a short buffer on the device (`click`); the person
 * started talking (`speech`); nothing was kept for a long while (`idle`); the person went into a
 * covered field, a password or card field, or left one (`covered`). The database column is built
 * from it too.
 */
export const CAPTURE_TRIGGERS = ["context", "click", "speech", "idle", "covered"] as const;
export const captureTriggerSchema = z.enum(CAPTURE_TRIGGERS);
export type CaptureTrigger = z.infer<typeof captureTriggerSchema>;

/** Each trigger's reason, as the package says why a screen was kept. */
export const CAPTURE_TRIGGER_REASONS = {
  context: "Moved to another app, window or page",
  click: "Just before a click",
  speech: "Started talking",
  idle: "After a long pause",
  covered: "Entered or left a covered field",
} as const satisfies Record<CaptureTrigger, string>;

/**
 * What the focused field holds, as the device classified it before dropping the value (ENG-1026):
 * `text` is free text, `choice` a menu, check box or radio button, and `secret` a password or card
 * field, whose value is never read. The trace carries a field's kind, never its value.
 */
export const VALUE_KINDS = [
  "amount",
  "date",
  "email",
  "phone",
  "number",
  "reference",
  "choice",
  "text",
  "empty",
  "secret",
] as const;
export const valueKindSchema = z.enum(VALUE_KINDS);
export type ValueKind = z.infer<typeof valueKindSchema>;

/** Where the person was: the front app, its focused window's title, and the page when it has one. */
export const screenContextSchema = z.strictObject({
  app: z.string().min(1).max(120),
  /** Empty when the window has no title or it could not be read in time. */
  window: z.string().max(300),
  url: z
    .url({ protocol: /^https?$/ })
    .max(2048)
    .optional(),
});
export type ScreenContext = z.infer<typeof screenContextSchema>;

const atSchema = z.int().nonnegative();
const sideSchema = z.int().positive().max(SCREEN_SIDE_MAX);
const coveredSchema = z.array(regionSchema).max(SCREEN_COVERED_MAX);

/**
 * A small copy of a screen for the timeline and for stepping, made on the device from the same
 * covered image, so it hides what the screen hides. At most `SCREEN_THUMBNAIL_WIDTH` wide.
 */
export const screenThumbnailInputSchema = z.strictObject({
  size: z.int().min(1).max(FILE_MAX_BYTES),
  mediaType: z.literal("image/jpeg"),
  width: z.int().positive().max(SCREEN_THUMBNAIL_WIDTH),
  height: sideSchema,
});
export type ScreenThumbnailInput = z.infer<typeof screenThumbnailInputSchema>;

/**
 * What the desktop app sends to begin a screen's upload. The image was covered before it was
 * encoded, so `covered` records what was hidden, not something left for the server to do. `key`
 * is the app's own ID for the screen: sending it again answers what the first did.
 */
export const screenStartInputSchema = z.strictObject({
  key: callerKeySchema,
  /** When the screen was grabbed, in epoch milliseconds. */
  at: atSchema,
  trigger: captureTriggerSchema,
  context: screenContextSchema,
  covered: coveredSchema,
  /**
   * False when the fields could not be checked in time (D5): the screen is kept and flagged as
   * possibly showing something sensitive, so the person can remove it.
   */
  checked: z.boolean(),
  size: z.int().min(1).max(FILE_MAX_BYTES),
  mediaType: z.literal("image/jpeg"),
  width: sideSchema,
  height: sideSchema,
  /** Sent by desktop apps from November 2026; a screen without one is read at full size. */
  thumbnail: screenThumbnailInputSchema.optional(),
});
export type ScreenStartInput = z.infer<typeof screenStartInputSchema>;

/**
 * A kept screen as anyone who may open the chat sees it. Its image is read through the files API,
 * and so is its thumbnail, `thumbnailFileId`, null for a screen kept without one: readers then
 * fall back to the full image.
 */
export const screenViewSchema = z.strictObject({
  id: idSchema("recordingScreen"),
  fileId: idSchema("file"),
  thumbnailFileId: idSchema("file").nullable(),
  at: atSchema,
  trigger: captureTriggerSchema,
  context: screenContextSchema,
  covered: coveredSchema,
  checked: z.boolean(),
  width: sideSchema,
  height: sideSchema,
});
export type ScreenView = z.infer<typeof screenViewSchema>;

/**
 * The answer to `startScreen`: the pending screen and where to send its bytes, as files `start`.
 * The same key again signs a fresh upload while the screen is pending; once it is complete, the
 * upload is null (a stored file is never written again) and the sender goes on to `completeScreen`.
 * `thumbnailUpload` is there only when the start sent a thumbnail: installed desktop apps check
 * this answer strictly, so one that sends none never sees the key.
 */
export const startedScreenSchema = z.strictObject({
  screen: z.strictObject({ id: idSchema("recordingScreen") }),
  upload: uploadTargetSchema.nullable(),
  thumbnailUpload: uploadTargetSchema.nullable().optional(),
});
export type StartedScreen = z.infer<typeof startedScreenSchema>;

/**
 * The answer to `completeScreen`: the screen, ready, and its `screen.action` on the tape. The
 * screen leaves out its thumbnail: installed desktop apps check this answer strictly, and one from
 * before thumbnails would refuse the key and send the screen again forever.
 */
export const completedScreenSchema = z.strictObject({
  screen: screenViewSchema.omit({ thumbnailFileId: true }),
  /** Where its entry is on the tape, or null when the tape dropped it. */
  seq: z.int().positive().nullable(),
  /** This key's screen was completed before; nothing new was written. */
  duplicate: z.boolean(),
});
export type CompletedScreen = z.infer<typeof completedScreenSchema>;

/** A recording's kept screens, oldest first; removed ones are left out. */
export const screenListSchema = z.strictObject({ screens: z.array(screenViewSchema) });
export type ScreenList = z.infer<typeof screenListSchema>;

/**
 * Lets the desktop app add screens to one recording, sent as `Authorization: Bearer <token>`. The
 * token is opaque to every reader; the API keeps only a hash of its secret. `expiresAt` is in epoch
 * milliseconds.
 */
export const captureGrantSchema = z.strictObject({
  token: z.string().min(1).max(200),
  expiresAt: atSchema,
});
export type CaptureGrant = z.infer<typeof captureGrantSchema>;

/**
 * What Abacor sees during a recording (ENG-1031): one display, or some apps. The person chooses at
 * the start of every recording and can change it while recording. Some things are never seen,
 * whatever is chosen (`NEVER_SEEN_BUNDLE_IDS`, private windows, Abacor itself); the desktop app
 * enforces the choice in the pixels it grabs, not only in what it records about them.
 */
export const sharedAppSchema = z.strictObject({
  /** The app's bundle identifier on macOS: how the grab includes its windows. */
  bundleId: z.string().min(1).max(255),
  name: z.string().min(1).max(120),
});
export type SharedApp = z.infer<typeof sharedAppSchema>;

/** The most apps one recording may share. */
export const SHARED_APPS_MAX = 32;

export const captureShareSchema = z.discriminatedUnion("kind", [
  /** One display, by its system ID: everything on it except what is never seen. */
  z.strictObject({ kind: z.literal("display"), displayId: z.int().nonnegative() }),
  /** Only these apps' windows, on whichever display the person works on; their new windows too. */
  z.strictObject({
    kind: z.literal("apps"),
    apps: z.array(sharedAppSchema).min(1).max(SHARED_APPS_MAX),
  }),
]);
export type CaptureShare = z.infer<typeof captureShareSchema>;

/**
 * Apps that are never seen and never listed (ENG-1031): password managers and the system's own
 * password and Touch ID prompts. The desktop app leaves their windows out of every grab and keeps
 * nothing while one is in front. Matched by bundle identifier, exactly.
 */
export const NEVER_SEEN_BUNDLE_IDS: readonly string[] = [
  // Password managers.
  "com.1password.1password",
  "com.agilebits.onepassword7",
  "com.agilebits.onepassword-osx",
  "com.apple.Passwords",
  "com.apple.keychainaccess",
  "com.bitwarden.desktop",
  "com.dashlane.dashlanephonefinal",
  "com.dashlane.Dashlane",
  "com.lastpass.LastPass",
  "com.lastpass.lastpassmacdesktop",
  "org.keepassxc.keepassxc",
  "com.keepersecurity.passwordmanager",
  "in.sinew.Enpass-Desktop",
  "com.nordsec.nordpass",
  "me.proton.pass.electron",
  "com.roboform.RoboForm",
  // The system's password, Touch ID and sign-in prompts.
  "com.apple.SecurityAgent",
  "com.apple.LocalAuthentication.UIAgent",
  "com.apple.coreautha",
  "com.apple.loginwindow",
];

/**
 * How a browser marks a private window, as macOS Accessibility reports the window (ENG-1031,
 * measured on macOS 26.6.2): Chrome and other Chromium browsers end its title with "(Incognito)",
 * "(InPrivate)" or "(Private)"; Safari with ", Private Browsing"; Firefox with "Private Browsing";
 * Arc names it in its identifier (`bigIncognitoBrowserWindow-…`). ScreenCaptureKit's own window
 * titles carry none of these, so the helper reads them through Accessibility.
 */
export const PRIVATE_WINDOW_TITLE = String.raw`(\((Incognito|InPrivate|Private)\)|Private Browsing)\s*$`;
export const PRIVATE_WINDOW_IDENTIFIER = String.raw`Incognito|InPrivate|PrivateBrowsing`;

/** Whether a window, as Accessibility describes it, is a private browser window. */
export function isPrivateWindow(window: { title: string; identifier?: string | null }): boolean {
  return (
    new RegExp(PRIVATE_WINDOW_TITLE, "i").test(window.title) ||
    (!!window.identifier && new RegExp(PRIVATE_WINDOW_IDENTIFIER, "i").test(window.identifier))
  );
}

/**
 * Browsers whose windows the helper reads through Accessibility before each grab, to leave their
 * private windows out. A private window of a browser not listed here is not recognized.
 */
export const BROWSER_BUNDLE_IDS: readonly string[] = [
  "com.google.Chrome",
  "com.google.Chrome.beta",
  "com.google.Chrome.dev",
  "com.google.Chrome.canary",
  "org.chromium.Chromium",
  "com.microsoft.edgemac",
  "com.brave.Browser",
  "company.thebrowser.Browser",
  "com.vivaldi.Vivaldi",
  "com.operasoftware.Opera",
  "com.apple.Safari",
  "com.apple.SafariTechnologyPreview",
  "org.mozilla.firefox",
  "org.mozilla.firefoxdeveloperedition",
  "app.zen-browser.zen",
  "com.kagi.kagimacOS",
];

/**
 * What capture adds to the tape apart from screens, the tape's own events, so the session folds
 * them: the person moved into something that is not shared, or back (`screen.unshared`, ENG-1031);
 * the person went into a covered field, a password or card field, or left it (`screen.masked`,
 * ENG-1026).
 */
export const captureEventSchema = z.discriminatedUnion("type", [
  screenMaskedEventSchema,
  screenUnsharedEventSchema,
]);
export type CaptureEvent = z.infer<typeof captureEventSchema>;

/** What the desktop app sends to add a capture event; `key` makes a resend land once. */
export const captureEventInputSchema = z.strictObject({
  key: callerKeySchema,
  /** When it happened on the device, in epoch milliseconds. */
  at: atSchema,
  event: captureEventSchema,
});
export type CaptureEventInput = z.infer<typeof captureEventInputSchema>;

/** The answer to `addEvent`: where it is on the tape (null when the tape dropped it). */
export const addedCaptureEventSchema = z.strictObject({
  seq: z.int().positive().nullable(),
  duplicate: z.boolean(),
});
export type AddedCaptureEvent = z.infer<typeof addedCaptureEventSchema>;

/**
 * The light action trace (ENG-1027): what the person did during a recording, as facts with no
 * content. It is its own append-only record per recording, keyed by the sender's key, not the
 * interview tape; the judgments see only the part of it the API puts on the tape as
 * `screen.action`.
 *
 * - `app`: the front app and its focused window's title changed.
 * - `page`: the address of the page in front changed, as the browser committed it (read through
 *   Accessibility, never from an address bar being typed in). The full address is kept, query and
 *   fragment included (D8, decided 30 October 2026); it can be reduced later.
 * - `field`: the focused field changed: its role, its name and the kind of value it holds, never
 *   the value.
 * - `click`: a mouse button went down, with the label and role of what was under it, where it was
 *   on the display, and never what a text field under it holds.
 * - `copy`: something was copied. Only that it happened: the clipboard's content is never read.
 * - `download`: a file arrived in the Downloads folder: its name and type.
 */

/** The longest page address the trace keeps; a longer one is cut there. */
export const PAGE_ADDRESS_MAX = 4096;
/** The longest label of a field or a click target the trace keeps. */
export const TRACE_LABEL_MAX = 120;
/** The most trace events one call adds. */
export const TRACE_BATCH_MAX = 100;

export const TRACE_EVENT_TYPES = ["app", "page", "field", "click", "copy", "download"] as const;
export type TraceEventType = (typeof TRACE_EVENT_TYPES)[number];

/** An Accessibility role as the system names it, such as `AXButton`. */
const roleSchema = z.string().min(1).max(64);
const labelSchema = z.string().min(1).max(TRACE_LABEL_MAX);

const appTraceSchema = z.strictObject({
  type: z.literal("app"),
  app: screenContextSchema.shape.app,
  window: screenContextSchema.shape.window,
});
const pageTraceSchema = z.strictObject({
  type: z.literal("page"),
  address: z.string().min(1).max(PAGE_ADDRESS_MAX),
});

/**
 * What the person did, as opposed to where they were (`app`, `page`): the package's Actions track
 * (ENG-1028) holds these.
 */
export const traceActionSchema = z.discriminatedUnion("type", [
  z.strictObject({
    type: z.literal("field"),
    role: roleSchema.nullable(),
    /** What names the field (its label, title or placeholder), or null when nothing does. */
    label: labelSchema.nullable(),
    kind: valueKindSchema.nullable(),
  }),
  z.strictObject({
    type: z.literal("click"),
    button: z.enum(["left", "right", "other"]),
    label: labelSchema.nullable(),
    role: roleSchema.nullable(),
    /** Where on the display, in fractions of its width and height from the top left. */
    x: fraction,
    y: fraction,
  }),
  z.strictObject({ type: z.literal("copy") }),
  z.strictObject({
    type: z.literal("download"),
    name: z.string().min(1).max(255),
    /** Its media type from the file name's extension, or null when the system knows none. */
    mediaType: z.string().min(1).max(255).nullable(),
  }),
]);
export type TraceAction = z.infer<typeof traceActionSchema>;

export const traceEventSchema = z.discriminatedUnion("type", [
  appTraceSchema,
  pageTraceSchema,
  ...traceActionSchema.options,
]);
export type TraceEvent = z.infer<typeof traceEventSchema>;

/**
 * One trace event as the desktop app sends it. `key` makes a resend land once; `screen` is the key
 * of the screen the app kept last before it, so the package and the tape can show that screen.
 */
export const traceItemSchema = z.strictObject({
  key: callerKeySchema,
  /** When it happened on the device, in epoch milliseconds. */
  at: atSchema,
  event: traceEventSchema,
  screen: callerKeySchema.optional(),
});
export type TraceItem = z.infer<typeof traceItemSchema>;

export const traceBatchInputSchema = z.strictObject({
  items: z.array(traceItemSchema).min(1).max(TRACE_BATCH_MAX),
});
export type TraceBatchInput = z.infer<typeof traceBatchInputSchema>;

/** The answer to `appendTrace`: how many were new, and how many had been added before. */
export const appendedTraceSchema = z.strictObject({
  added: z.int().nonnegative(),
  duplicates: z.int().nonnegative(),
});
export type AppendedTrace = z.infer<typeof appendedTraceSchema>;
